import { isPlatform } from '@ionic/core';
import { useCallback, useEffect, useState } from 'react';

const getMediaQueryMatch = (query: string): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(query).matches;
};

/**
 * A hook which tests the provided media query, and updates when the test result changes.
 * See {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia} for the format
 * with which to provide the query string.
 *
 * @param query The media query to test.
 * @returns Whether or not the provided query matched.
 */
export const useMediaQuery = (query: string): boolean => {
  const [doesMatch, setDoesMatch] = useState<boolean>(getMediaQueryMatch(query));

  const handleChange = useCallback(() => {
    setDoesMatch(getMediaQueryMatch(query));
  }, [query]);

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    handleChange();

    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [handleChange, query]);

  return doesMatch;
};

/**
 * A dictionary of breakpoints, these are set by Ionic.
 * @see https://ionicframework.com/docs/api/grid#default-breakpoints
 */
const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
} as const;

type Breakpoint = keyof typeof breakpoints;

/**
 * A hook which tests whether the current viewport has hit the provided breakpoint.
 *
 * @param breakpoint The breakpoint to test. These are defined by Ionic, see {@link https://ionicframework.com/docs/api/grid#default-breakpoints}.
 * @param isLargerThan Whether or not we are testing that the viewport is larger than the provided breakpoint. Defaults to true,
 * otherwise we are testing that the viewport is smaller than the provided breakpoint.
 */
export const useBreakpoint = (breakpoint: Breakpoint, isLargerThan = true) => {
  const query = isLargerThan ? 'min-width' : 'max-width';
  const value = isLargerThan ? breakpoints[breakpoint] : breakpoints[breakpoint] - 1;
  return useMediaQuery(`(${query}: ${value}px)`);
};

type BreakpointMap<T> = Partial<Record<Breakpoint, T>> & { xs: T };

/**
 * A hook which takes a record of breakpoints to value mappings, and returns the value associated
 * with the active breakpoint. If no value is defined in the map for that breakpoint, the next
 * matching breakpoint is used.
 *
 * @param mappings The record of breakpoint names to values. Note that this object does not need to
 * be reference stable, but the values within should be.
 */
export const useBreakpointMap = <T extends any>(mappings: BreakpointMap<T>): T => {
  const xs = mappings.xs;
  const sm = mappings.hasOwnProperty('sm') ? mappings.sm! : xs;
  const md = mappings.hasOwnProperty('md') ? mappings.md! : sm;
  const lg = mappings.hasOwnProperty('lg') ? mappings.lg! : md;
  const xl = mappings.hasOwnProperty('xl') ? mappings.xl! : lg;

  const isSm = useBreakpoint('sm');
  const isMd = useBreakpoint('md');
  const isLg = useBreakpoint('lg');
  const isXl = useBreakpoint('xl');

  if (isXl) return xl;
  if (isLg) return lg;
  if (isMd) return md;
  if (isSm) return sm;
  return xs;
};

/**
 * A hook which returns whether the current device is in the capacitor runtime environment.
 */
export const useIsMobile = () => {
  return isPlatform('capacitor');
};

/** A hook which returns whether the current device has a touchscreen. */
export const useHasTouchscreen = () => {
  return useMediaQuery('(any-pointer: coarse)');
};

/**
 * A hook which returns whether the current runtime environment has access to the BLE APIs.
 */
export const useHasBluetoothLE = () => {
  // Currently, we just check for whether we are running in a native capacitor app. The electron app behaves as web does.
  return isPlatform('capacitor');
};

/**
 * A hook which returns whether the current runtime environment supports printing.
 */
export const useCanPrint = () => {
  return !isPlatform('capacitor');
};
